/**
 * Workshop cancel API client. Split out of `workshops.ts` to keep that module
 * under the max-lines cap; re-exported from there so callers can keep importing
 * from `../api/workshops`.
 *
 * A cancel moves every confirmed booking to one target workshop at the same
 * price — no refunds. On any refusal the POST throws {@link CancelWorkshopError}
 * carrying the machine `code`; nothing was changed.
 */
import { apiFetch } from '../lib/apiFetch';

import type { WorkshopPendingSales } from './rebooking';

/** Machine-readable refusal codes from `POST /workshops/:id/cancel`. */
export type CancelWorkshopErrorCode =
  | 'SOURCE_NOT_FOUND'
  | 'SOURCE_NOT_DISABLED'
  | 'PENDING_SALES'
  | 'BOOKINGS_CHANGED'
  | 'TARGET_REQUIRED'
  | 'TARGET_NOT_FOUND'
  | 'TARGET_IS_SOURCE'
  | 'TARGET_NOT_BOOKABLE'
  | 'TARGET_REGION_MISMATCH'
  | 'TARGET_PRICE_MISMATCH'
  | 'NO_CAPACITY'
  | 'ALREADY_REBOOKED'
  | 'NOT_CONFIRMED'
  | 'CONCURRENT_MODIFICATION';

export class CancelWorkshopError extends Error {
  code: CancelWorkshopErrorCode | string;
  status: number;
  /** For `PENDING_SALES`: what still has to settle. */
  pendingSales?: WorkshopPendingSales;
  constructor(
    code: CancelWorkshopErrorCode | string,
    message: string,
    status: number,
    pendingSales?: WorkshopPendingSales,
  ) {
    super(message);
    this.name = 'CancelWorkshopError';
    this.code = code;
    this.status = status;
    if (pendingSales) this.pendingSales = pendingSales;
  }
}

async function readCancelError(res: Response): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as {
    code?: string;
    message?: string;
    details?: WorkshopPendingSales;
  };
  const message = body.message ?? `Request failed: ${res.status}`;
  const pending = body.code === 'PENDING_SALES' ? body.details : undefined;
  throw new CancelWorkshopError(body.code ?? `HTTP_${res.status}`, message, res.status, pending);
}

/** Body for `POST /workshops/:id/cancel`. */
export interface CancelWorkshopBody {
  /** Required when the workshop has bookings to move. */
  targetWorkshopId?: string;
  /** Recorded on every moved booking and the workshop's history. */
  reason: string;
}

/** Success payload from `POST /workshops/:id/cancel`. */
export interface CancelWorkshopResult {
  workshop: { id: string; lifecycle: 'cancelled' };
  moved: { bookings: number; attendees: number };
  target: {
    id: string;
    startsAt: string;
    localStartsAt: string | null;
    venueName: string;
    workshopName: string;
  } | null;
  checkoutsCancelled: number;
  /** Rebook emails sent after commit; `failed > 0` is a soft warning — those
   *  bookings were flagged for review, and the cancel still happened. */
  notifications: { sent: number; failed: number };
}

export async function cancelWorkshop(
  id: string,
  body: CancelWorkshopBody,
): Promise<CancelWorkshopResult> {
  const res = await apiFetch(`/api/v1/workshops/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) await readCancelError(res);
  return res.json();
}
